import { Experiment, Source } from '@amplitude/experiment-js-client';

import { getAmplitudeDeploymentKeyClient } from 'chairisher/context/analytics';
import {
    getAmplitudeExperimentBucketMap,
    getAmplitudeExperimentKeyToVariantMap,
    getExposeExperimentFlags,
} from 'chairisher/context/experiments';
import { handleAsyncError } from 'chairisher/util/logging';

let experimentClient = null;

/**
 * Initializes and sets the Amplitude Experiments client with the values rendered by the server.
 */
export function initializeExperiments() {
    try {
        const deploymentKey = getAmplitudeDeploymentKeyClient();
        const experimentKeyToVariantMap = getAmplitudeExperimentKeyToVariantMap();
        experimentClient = Experiment.initializeWithAmplitudeAnalytics(deploymentKey, {
            initialVariants: experimentKeyToVariantMap,
            source: Source.InitialVariants,
        });
    } catch (error) {
        console.error('Failed to initialize Amplitude Experiments client:', error);
        handleAsyncError(error);
    }
}

/**
 * Returns the variant object for an experiment based on the provided key.
 *
 * @param {string} key The key to identify the experiment
 * @returns {object|null} The variant object for the experiment, or null if the experiment client is not initialized or an error occurs
 */
export function getExperimentVariantObj(key) {
    try {
        if (!experimentClient) {
            console.error('Amplitude Experiments client not initialized');
            return null;
        }

        return experimentClient.variant(key);
    } catch (error) {
        console.error('Failed to get experiment variant:', error);
        handleAsyncError(error);
        return null;
    }
}

/**
 * Ensures an $exposure event is fired for an experiment.
 * This action occurs when experimentClient.variant() is called as part of getExperimentVariantObj().
 *
 * This should only be used for server-side experiments that do not involve the front-end,
 * but where we still want to ensure the user receives the correct analytics tracking and user properties.
 *
 * Note: We ignore the returned value, as we are only interested in the side effects of the call.
 *
 * @param {string} key The key to identify the experiment
 */
export function exposeExperiment(key) {
    getExperimentVariantObj(key);
}

/**
 * Fires an $exposure event for each experiment flag provided by the server and set in page context.
 * The list should only be populated with flags that require an exposure for that specific page view.
 */
export function exposeExperiments() {
    getExposeExperimentFlags().forEach((flagKey) => exposeExperiment(flagKey));
}

/**
 * Returns the variant value for an experiment based on the provided key.
 *
 * @param {string} key The key to identify the experiment
 * @returns {string|undefined} The variant value for the experiment, or undefined if not found
 */
export function getExperimentVariantValue(key) {
    return getExperimentVariantObj(key)?.value;
}

/**
 * Checks if the user is assigned a specific experiment variant value.
 *
 * @param {string} key The key to identify the experiment
 * @param {string} value The value of the experiment variant to check
 * @returns {boolean} True indicates the user is assigned the value, false otherwise
 */
function isExperimentVariant(key, value) {
    return getExperimentVariantValue(key) === value;
}

/**
 * Checks if the user is in variant b for a given experiment.
 *
 * @param {string} key The key to identify the experiment
 * @returns {boolean} True indicates the user is in experiment variant b, false otherwise
 */
export function isExperimentVariantB(key) {
    return isExperimentVariant(key, getAmplitudeExperimentBucketMap().B);
}

/**
 * Checks if the user is in variant c for a given experiment.
 *
 * @param {string} key The key to identify the experiment
 * @returns {boolean} True indicates the user is in experiment variant c, false otherwise
 */
export function isExperimentVariantC(key) {
    return isExperimentVariant(key, getAmplitudeExperimentBucketMap().C);
}
